import type { FC } from 'react';
import React, { useMemo, useState } from 'react';
import type { WebsiteHeaderLayout_VideoBackground } from '@shared/types/WebsiteHeaderSettings.types';
import { Box, Flex, styled, useToast } from '@withjoy/joykit';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';
import { AnimatedImageBox } from '@apps/guest/packages/layout-engine/widgets/Custom/components/PhotoTrio/PhotoTrio.styles';
import { BouncingChevron } from '../components/BouncingChevron/BouncingChevron';
import type { EventNameNavForStandaloneHeaderProps } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import { EventNameNavForStandaloneHeader } from '../components/EventNameNavForStandaloneHeader/EventNameNavForStandaloneHeader';
import type { EventPageType } from '@graphql/generated-base';
import { BrannanNavigation } from '@graphql/generated-base';
import { pxToRem } from '@withjoy/joykit/theme';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { GuestSiteEventDesign } from '../../../../layout.types';
import { addError } from '@shared/utils/logger';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { extractVimeoVideoId, extractYoutubeVideoId, isVimeoUrl, isYoutubeUrl } from '@shared/utils/video';
import { useTranslation } from '@shared/core';
import { isInIframe } from '@shared/utils/isInIframe';
import { useColorPaletteProvider } from '../../../../ColorPaletteProvider/ColorPaletteProvider';
import { useSkipInitialAnimation } from '@apps/guest/packages/layout-engine/hooks/useSkipInitialAnimation';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { usePremiumHeaderLayoutContext } from '@apps/guest/routes/GuestSite/hooks/usePremiumHeaderLayout';
import { useWebsitePublishCapabilityFeature } from '@shared/hooks/useWebsitePublishCapabilityFeature';
import { usePaidFeature } from '@shared/hooks/usePaidFeature';
import { YoutubeVideoContainer } from '../components/YoutubeVideoContainer/YoutubeVideoContainer';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';

export type VideoBackgroundLayoutProps = Readonly<
  {
    websiteHeader: WebsiteHeaderLayout_VideoBackground;
    showSkeleton?: boolean;
    showPlaceholder?: boolean;
    currentPageType: EventPageType | undefined;
    eventDesign: GuestSiteEventDesign;
    isInlineEditing?: boolean;
    renderedContainerHeight?: number;
    renderedContainerWidth?: number;
    editableContentRef?: React.RefObject<HTMLDivElement>;
    forceRenderViewIn?: 'mobile' | 'desktop';
    scaleValue?: number;
  } & EventNameNavForStandaloneHeaderProps
>;

export type VideoBackgroundRendererProps = Readonly<{
  videoUrl?: string;
  showPlaceholder?: boolean;
  skipAnimation?: boolean;
  thumbnailUrl?: string;
  height: string;
  isCompactView: boolean;
  isMobile: boolean;
  currentPageType: EventPageType | undefined;
  pageSlug: string | undefined;
  handleEditVideoClick: () => void;
  children: React.ReactNode;
}>;

/* Sits above art sprites (docked+1) that live inside FrameOuter, which is a sibling
   rendered after StandaloneWebsiteHeader in the DOM. */
const VideoBackgroundOuter = styled(Box)`
  z-index: ${({ theme }) => theme.zIndices.docked + 2};
`;

const CONTAINER_ASPECT_RATIO = PHOTO_PRESENTATION_CONFIG.videoBackground.aspectRatio as number;

type Video = Readonly<{
  isVimeo: false;
  isYoutube: false;
  videoUrl: string;
}>;

type YoutubeVideo = Readonly<{
  isYoutube: true;
  videoId: string;
  videoUrl: string;
}>;

type VimeoVideo = Readonly<{
  isVimeo: true;
  videoUrl: string;
}>;

type PreparedVideo = Video | YoutubeVideo | VimeoVideo;

export const VideoBackgroundRenderer: FC<VideoBackgroundRendererProps> = props => {
  const { tq } = useTranslation('videoBackground');
  const { videoUrl, showPlaceholder = false, skipAnimation, thumbnailUrl, height, isCompactView, isMobile, currentPageType, pageSlug, handleEditVideoClick, children } = props;
  const { pageColor } = useColorPaletteProvider();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const isPreviewing = isInIframe();
  const { toast } = useToast();

  // 16:9 landscape for desktop, 3:4 portrait for mobile
  const videoMinWidth = isMobile ? '75vh' : '177.78vh';
  const videoMinHeight = isMobile ? '133.33vw' : '56.25vw';

  const handleIframeLoad = useEventCallback(() => {
    setIsLoading(false);
  });

  const handleIframeError = useEventCallback(() => {
    setIsLoading(false);
    setError(true);
  });

  const premiumHeaderLayout = usePremiumHeaderLayoutContext();
  const websitePublishCapabilityFeature = useWebsitePublishCapabilityFeature();
  const videoBackgroundFeature = usePaidFeature('websiteVideoHeader');

  const preparedVideo: PreparedVideo | null = useMemo(() => {
    if (!videoUrl) {
      return null;
    }

    const isWebsitePublished = websitePublishCapabilityFeature.eventPublishedState.value && !websitePublishCapabilityFeature.eventPublishedState.isLoading;
    const isExposedToWebsiteVisibility = websitePublishCapabilityFeature.eventExposure.value && !websitePublishCapabilityFeature.eventExposure.isLoading;
    const hasPurchasedVideoBackground = videoBackgroundFeature.value;
    const hasAutoopenedUpgradeDialog = premiumHeaderLayout.dialog.isOpen && premiumHeaderLayout.dialog.secondaryAction === 'turn-off';

    if (hasPurchasedVideoBackground || hasAutoopenedUpgradeDialog || isPreviewing || (isExposedToWebsiteVisibility && !isWebsitePublished)) {
      try {
        if (isVimeoUrl(videoUrl)) {
          const searchParams = new URL(videoUrl).searchParams;

          [
            ['background', '1'],
            ['autoplay', '1'],
            ['loop', '1'],
            ['muted', '1'],
            ['playsinline', '1'],
          ].forEach(([key, value]) => {
            if (searchParams.has(key)) {
              return;
            }

            searchParams.set(key, value);
          });

          const videoId = extractVimeoVideoId(videoUrl);

          const video: VimeoVideo = {
            isVimeo: true,
            videoUrl: `https://player.vimeo.com/video/${videoId}?${searchParams.toString()}`,
          };

          return video;
        }
        if (isYoutubeUrl(videoUrl)) {
          const videoId = extractYoutubeVideoId(videoUrl);

          if (!videoId) {
            return null;
          }

          const video: YoutubeVideo = {
            isYoutube: true,
            videoId,
            videoUrl,
          };

          return video;
        }

        const video: Video = {
          isVimeo: false,
          isYoutube: false,
          videoUrl,
        };

        return video;
      } catch (error) {
        if (isPreviewing) {
          toast(tq`We couldn't load your video background.`);
        }

        addError(error, { context: 'VideoBackgroundRenderer#preparedVideoUrl' });

        return null;
      }
    }

    return null;
  }, [videoUrl, isPreviewing, toast, tq, premiumHeaderLayout, websitePublishCapabilityFeature, videoBackgroundFeature]);

  const showVideo = !isLoading && !error && !!preparedVideo;

  return (
    <AnimatedImageBox
      flexDirection="column"
      alignItems="center"
      width="100%"
      height={height}
      aspectRatio={isCompactView ? 'unset' : `${CONTAINER_ASPECT_RATIO}`}
      overflow="hidden"
      backgroundColor={skipAnimation ? 'transparent' : showPlaceholder ? 'lightgray' : pageColor}
      skipAnimation={skipAnimation}
      minHeight={pxToRem(96)}
    >
      <InlineEditor
        elementLabel={tq`Video`}
        wrapperType="actionInside"
        actionData={{ editVideo: handleEditVideoClick }}
        componentName="headerVideo"
        pageName={currentPageType}
        pageSlug={pageSlug || ''}
        wrapperCSS={{ width: '100%', height: '100%' }}
        isEligibleForInlineEditing={!isCompactView}
      >
        {thumbnailUrl && (
          <Box
            as="img"
            src={thumbnailUrl}
            alt={tq`Video Background Thumbnail`}
            position="absolute"
            opacity={showVideo ? 0 : 1}
            transition="opacity 0.3s ease-in-out"
            top={0}
            left={0}
            width="100%"
            height="100%"
            objectFit="cover"
            zIndex={1}
          />
        )}

        {!!preparedVideo &&
          ('isYoutube' in preparedVideo && preparedVideo.isYoutube ? (
            <YoutubeVideoContainer
              videoId={preparedVideo.videoId}
              videoUrl={preparedVideo.videoUrl}
              showVideo={showVideo}
              onReady={handleIframeLoad}
              onError={handleIframeError}
              minWidth={videoMinWidth}
              minHeight={videoMinHeight}
            />
          ) : (
            <Box
              as="iframe"
              title={tq`Video Background`}
              src={preparedVideo.videoUrl}
              allow="autoplay; fullscreen; picture-in-picture"
              position="absolute"
              top="50%"
              left="50%"
              border="none"
              outline="none"
              transform="translate(-50%, -50%)"
              opacity={showVideo ? 1 : 0}
              transition="opacity 0.3s ease-in-out"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              width="100vw"
              height="100vh"
              minWidth={videoMinWidth}
              minHeight={videoMinHeight}
              zIndex={1}
            />
          ))}
      </InlineEditor>

      <Box
        width="100%"
        height="100%"
        position={isCompactView ? 'relative' : 'absolute'}
        zIndex="banner"
        pointerEvents={isPreviewing ? 'none' : 'auto'}
        top={0}
      >
        {children}
      </Box>
    </AnimatedImageBox>
  );
};

export const VideoBackgroundLayout: FC<VideoBackgroundLayoutProps> = props => {
  const {
    websiteHeader,
    showSkeleton,
    showPlaceholder = false,
    currentPageType,
    isInlineEditing = false,
    renderedContainerHeight,
    renderedContainerWidth,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1,
    pages,
    pageSlug,
    ...restProps
  } = props;

  const { checkIsWelcomePage } = useWelcomePage();
  const { isWelcomePage } = checkIsWelcomePage(pageSlug, currentPageType);

  const pageIndex = useMemo(() => pages?.findIndex(p => p.pageSlug === pageSlug), [pages, pageSlug]);
  // compact view is only for brannan classic navigation layout
  const isCompactView = restProps.eventDesign?.brannanNavigation === BrannanNavigation.classic && !isWelcomePage && pageIndex !== 0;

  //  Hide video background in compact view when navbar is in menu variant
  // The videobackground is not needed for the menu variant as it is a hamburger menu
  // The hamburger menu is shown only in mobile devices
  const isMobileScreen = useIsMobileScreen();
  const skipAnimation = useSkipInitialAnimation();
  const [isBouncingChevronEnabled] = useIsFeatureEnabled('guestBackgroundHeaderBouncingChevronEnabled');

  const shouldHideVideoBackground = useMemo(() => {
    return isCompactView && restProps.navBarVariant === 'menu';
  }, [isCompactView, restProps.navBarVariant]);

  const { height, aspectRatio } = useMemo(
    () =>
      isCompactView
        ? { height: '100%', aspectRatio: 'unset' }
        : {
            height: isInlineEditing && renderedContainerHeight ? `${renderedContainerHeight}px` : isMobileScreen && isBouncingChevronEnabled ? '90svh' : '100svh',
            aspectRatio: `${CONTAINER_ASPECT_RATIO}`,
          },
    [isBouncingChevronEnabled, isCompactView, isInlineEditing, isMobileScreen, renderedContainerHeight],
  );

  const viewportHeightFallback = isMobileScreen && isBouncingChevronEnabled ? '90svh' : '100svh';

  const { video, headerTitleStyle, navigationStyle } = websiteHeader.headerPhoto;
  const videoUrl = isMobileScreen && video?.mobileUrl ? video.mobileUrl : video?.desktopUrl;

  // Memoize container dimensions to prevent unnecessary style recalculations
  // when hiding video background on mobile/tablet
  const { containerAspectRatio, containerHeight, containerMinHeight, containerOverflow } = useMemo(() => {
    return {
      containerAspectRatio: shouldHideVideoBackground ? 'unset' : aspectRatio,
      containerHeight: shouldHideVideoBackground ? 'auto' : height,
      containerMinHeight: shouldHideVideoBackground ? 'auto' : pxToRem(96),
      containerOverflow: shouldHideVideoBackground ? 'visible' : 'hidden',
    };
  }, [shouldHideVideoBackground, aspectRatio, height]);

  const handleEditTextClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editWebsiteHeaderCustomTitle',
        inlineEditData: { data: { ...props, placeholderImageUrl: video?.placeholderImageUrl }, validationConstraints: { maxLength: 30 } },
      },
    });
  });

  const handleEditVideoClick = useEventCallback(() => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderVideo' },
    });
  });

  return (
    <VideoBackgroundOuter
      position="relative"
      width="100%"
    >
      <Flex
        position="relative"
        width="100%"
        aspectRatio={containerAspectRatio}
        height={containerHeight}
        minHeight={containerMinHeight}
        overflow={containerOverflow}
      >
        {shouldHideVideoBackground ? (
          <EventNameNavForStandaloneHeader
            eventDisplayName={websiteHeader.eventDisplayName}
            colors={websiteHeader.headerPhoto.colors}
            headerTextData={websiteHeader.headerPhoto.eventName}
            isCompactView={isCompactView}
            currentPageType={currentPageType}
            handleEditTextClick={handleEditTextClick}
            isContentEditable={isInlineEditing}
            editableContentRef={editableContentRef}
            forceRenderViewIn={forceRenderViewIn}
            scaleValue={scaleValue}
            pages={pages}
            pageSlug={pageSlug}
            renderedContainerHeight={renderedContainerHeight}
            renderedContainerWidth={renderedContainerWidth}
            viewportHeightFallback={viewportHeightFallback}
            headerTitleStyle={headerTitleStyle}
            navigationStyle={navigationStyle}
            {...restProps}
          />
        ) : (
          <VideoBackgroundRenderer
            showPlaceholder={showPlaceholder}
            skipAnimation={skipAnimation}
            videoUrl={videoUrl}
            thumbnailUrl={video?.placeholderImageUrl}
            height={height}
            isCompactView={isCompactView}
            isMobile={isMobileScreen && !!video?.mobileUrl}
            currentPageType={currentPageType}
            pageSlug={pageSlug}
            handleEditVideoClick={handleEditVideoClick}
          >
            <EventNameNavForStandaloneHeader
              eventDisplayName={websiteHeader.eventDisplayName}
              colors={websiteHeader.headerPhoto.colors}
              headerTextData={websiteHeader.headerPhoto.eventName}
              isCompactView={isCompactView}
              currentPageType={currentPageType}
              handleEditTextClick={handleEditTextClick}
              isContentEditable={isInlineEditing}
              editableContentRef={editableContentRef}
              forceRenderViewIn={forceRenderViewIn}
              scaleValue={scaleValue}
              pages={pages}
              pageSlug={pageSlug}
              renderedContainerHeight={renderedContainerHeight}
              renderedContainerWidth={renderedContainerWidth}
              viewportHeightFallback={viewportHeightFallback}
              headerTitleStyle={headerTitleStyle}
              navigationStyle={navigationStyle}
              {...restProps}
            />
          </VideoBackgroundRenderer>
        )}
      </Flex>
      {isBouncingChevronEnabled && isMobileScreen && !isCompactView && !shouldHideVideoBackground && !isInlineEditing && <BouncingChevron />}
    </VideoBackgroundOuter>
  );
};

VideoBackgroundLayout.displayName = 'VideoBackgroundLayout';
